import { z } from 'zod';
import { blockTagId } from '../ids.js';
import { blockRowSchema, instantSchema, stageRowSchema, uuidSchema } from '../schema/rows.js';
import { capturedTagInput, tagWrites } from './capture.js';
import { fieldValueSnapshot, followUpWrites } from './follow-up.js';
import { highlightTypeInput, activeHighlightInput, setHighlight, } from './highlight.js';
import { DomainError } from './types.js';
/**
 * One triage decision on an Inbox element (docs/03 §4.6, docs/04 §2, TRI-02/03/06): destination,
 * accepted suggestions and, with ASAP, the TAI — all in one local transaction. It also returns
 * the operations that undo it (design D2), computed from the state read before deciding, so `Z`
 * is just another local transaction.
 */
const stageInput = stageRowSchema.pick({
    id: true,
    workspace_id: true,
    category: true,
    kind_scope: true,
});
const decisionInput = z.discriminatedUnion('type', [
    /** A, B, G: open task in that stage. */
    z.object({ type: z.literal('stage'), stage: stageInput }),
    /** S: open task in the waiting stage, waiting on a person until a follow-up date. */
    z.object({
        type: z.literal('waiting'),
        stage: stageInput,
        waiting_on: capturedTagInput,
        follow_up_on: blockRowSchema.shape.due_on.unwrap(),
        fields: z.object({ waiting_on: uuidSchema, follow_up_on: uuidSchema }),
        /** Values the block already had for those fields, restored by the undo. */
        previous: z.array(fieldValueSnapshot).default([]),
    }),
    /** H: done task, without going through ASAP (stays in its stage). */
    z.object({ type: z.literal('done') }),
    /** X: to the trash. */
    z.object({ type: z.literal('trash') }),
    /** Enter: only the accepted suggestions; the element stays in the Inbox. */
    z.object({ type: z.literal('edit') }),
]);
const triageItemInput = z.object({
    user_id: uuidSchema,
    item: blockRowSchema.pick({
        id: true,
        workspace_id: true,
        kind: true,
        stage_id: true,
        stage_changed_at: true,
        status: true,
        completed_at: true,
        deleted_at: true,
        title: true,
        due_on: true,
        start_on: true,
    }),
    decision: decisionInput,
    /** Live descendants of the element: X sends them to the trash with it (INV-12). */
    descendants: z.array(uuidSchema).default([]),
    /** Suggestions accepted with Enter or with the destination key (TRI-03). */
    edits: z
        .object({
        title: z.string().trim().min(1).max(2000).optional(),
        due_on: blockRowSchema.shape.due_on.optional(),
        start_on: blockRowSchema.shape.start_on.optional(),
        tags: z.array(capturedTagInput).default([]),
        /** Tags the element already carries: never joined again, never removed by the undo. */
        assigned_tag_ids: z.array(uuidSchema).default([]),
    })
        .optional(),
    /** `I` together with A (docs/03 §4.6): the TAI of the current period. */
    highlight: z
        .object({
        type: highlightTypeInput,
        period_id: uuidSchema,
        current: z.array(activeHighlightInput),
        mode: z.enum(['add', 'replace']),
        force: z.boolean().default(false),
    })
        .optional(),
    now: instantSchema.optional(),
});
export function triageItem(input) {
    const parsed = triageItemInput.parse(input);
    const { item, decision } = parsed;
    const workspaceId = item.workspace_id;
    const now = parsed.now ?? new Date().toISOString();
    const blockId = item.id;
    // ── Block columns of the decision ──────────────────────────────────────────────────────────
    const target = {};
    let stageCategory = null;
    if (decision.type === 'stage' || decision.type === 'waiting') {
        const { stage } = decision;
        if (stage.workspace_id !== workspaceId) {
            throw new DomainError('invariant_violation', 'La etapa es de otro espacio.', 'INV-11');
        }
        if (stage.kind_scope !== 'task' || stage.category === 'inbox') {
            throw new DomainError('invariant_violation', 'El triaje envía a etapas de tareas fuera del Inbox.', 'INV-04');
        }
        Object.assign(target, { kind: 'task', status: 'open', completed_at: null });
        if (stage.id !== item.stage_id) {
            Object.assign(target, { stage_id: stage.id, stage_changed_at: now });
        }
        stageCategory = stage.category;
    }
    else if (decision.type === 'done') {
        Object.assign(target, { kind: 'task', status: 'done', completed_at: now });
    }
    else if (decision.type === 'trash') {
        target.deleted_at = now;
    }
    const edits = decision.type === 'trash' ? undefined : parsed.edits;
    if (edits?.title !== undefined)
        target.title = edits.title;
    if (edits?.due_on !== undefined)
        target.due_on = edits.due_on;
    if (edits?.start_on !== undefined)
        target.start_on = edits.start_on;
    // Only the columns that actually change, and their previous values for the undo.
    const previous = item;
    const changed = Object.fromEntries(Object.entries(target).filter(([column, value]) => previous[column] !== value));
    const restored = Object.fromEntries(Object.keys(changed).map((c) => [c, previous[c] ?? null]));
    const operations = [];
    const undo = [];
    // ── Tags: accepted suggestions and the person of the follow-up ─────────────────────────────
    const suggested = tagWrites(workspaceId, edits?.tags ?? [], now);
    const person = decision.type === 'waiting' ? tagWrites(workspaceId, [decision.waiting_on], now) : undefined;
    const newTags = new Map();
    for (const op of [...suggested.tags, ...(person?.tags ?? [])])
        newTags.set(op.id, op);
    operations.push(...newTags.values());
    if (Object.keys(changed).length > 0) {
        operations.push({ table: 'blocks', op: 'PATCH', id: blockId, data: changed });
    }
    // X takes the descendants with the same mark, like `trashBlock` (f1-trash-search D1).
    const descendants = decision.type === 'trash' ? parsed.descendants : [];
    for (const id of descendants) {
        operations.push({ table: 'blocks', op: 'PATCH', id: id, data: { deleted_at: now } });
    }
    const assigned = new Set(edits?.assigned_tag_ids ?? []);
    const personLeaf = person?.leaves[0];
    const joins = suggested.leaves
        .filter((leaf) => !assigned.has(leaf.tagId))
        .map((leaf) => ({
        table: 'block_tags',
        op: 'PUT',
        id: blockTagId(blockId, leaf.tagId),
        data: {
            workspace_id: workspaceId,
            block_id: blockId,
            tag_id: leaf.tagId,
            taxonomy_id: leaf.taxonomyId,
            created_at: now,
        },
    }));
    operations.push(...joins);
    // ── Follow-up fields (S) ───────────────────────────────────────────────────────────────────
    const fieldUndo = [];
    if (decision.type === 'waiting') {
        if (!personLeaf) {
            throw new DomainError('missing_field', 'Indica a quién esperas.', 'SEG-01');
        }
        const fields = followUpWrites({
            workspace_id: workspaceId,
            block_id: blockId,
            fields: decision.fields,
            person_tag_id: personLeaf.tagId,
            follow_up_on: decision.follow_up_on,
            previous: decision.previous,
            now,
        });
        operations.push(...fields.operations);
        fieldUndo.push(...fields.undo);
    }
    // ── TAI (I + A) ────────────────────────────────────────────────────────────────────────────
    let highlightUndo = [];
    if (parsed.highlight) {
        if (decision.type !== 'stage') {
            throw new DomainError('invariant_violation', 'Solo las tareas en ASAP pueden ser TAI.', 'INV-07');
        }
        const highlight = setHighlight({
            user_id: parsed.user_id,
            type: parsed.highlight.type,
            period_id: parsed.highlight.period_id,
            block: { id: blockId, kind: 'task', stage_category: stageCategory },
            current: parsed.highlight.current,
            mode: parsed.highlight.mode,
            force: parsed.highlight.force,
            now,
        });
        // After the block: the server checks that the block is already in an eligible stage.
        operations.push(...highlight.operations);
        highlightUndo = highlight.undo;
    }
    // Undo in reverse dependency order (design D2): highlights, fields and joins, then the block.
    // New tags stay, like in the capture.
    undo.push(...highlightUndo, ...fieldUndo);
    undo.push(...joins.map((join) => ({ table: 'block_tags', op: 'DELETE', id: join.id })));
    if (Object.keys(restored).length > 0) {
        undo.push({ table: 'blocks', op: 'PATCH', id: blockId, data: restored });
    }
    // The descendants come back after their parent.
    undo.push(...descendants.map((id) => ({
        table: 'blocks',
        op: 'PATCH',
        id: id,
        data: { deleted_at: null },
    })));
    return { operations, undo };
}
//# sourceMappingURL=triage.js.map